import { z } from 'zod';
import { validatePhone } from '../../../../../shared/utils';

const validateLocationSchema = (
    val: z.infer<typeof BaseLocationSchema>,
    ctx: z.RefinementCtx,
) => {
    if (val.usesNirovision && val.nirovisionLocationId === '') {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'Required',
            path: ['nirovisionLocationId'],
        });
    }

    if (val.usesNirovision && val.nirovisionLabelId === '') {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'Required',
            path: ['nirovisionLabelId'],
        });
    }
};

const VehicleDetails = z
    .object({
        dropoffLocationSameAsPickup: z.boolean(),
        dropoffStreetAddress: z.string(),
        dropoffSuburb: z.string(),
        dropoffState: z
            .enum(['NSW', 'QLD', 'TAS', 'VIC', 'ACT', 'WA', 'SA', 'NT'], {
                message: 'Required',
            })
            .optional()
            .nullable(),
        dropoffCountryCode: z.enum(['AU'], { message: 'Required' }),
        dropoffPostcode: z
            .string()
            .max(4, { message: 'Invalid postcode' })
            .optional(),
        dropoffPhone: z
            .string()
            .refine(validatePhone, { message: 'Invalid phone number' })
            .or(z.literal('')),
        dropoffUsesNirovision: z.boolean({ message: 'Required' }),
        dropoffNirovisionLocationId: z.string(),
        dropoffNirovisionLabelId: z.string(),
        dropoffNirovisionVehicleLabelId: z.string(),
    })
    .superRefine((val, ctx) => {
        if (val.dropoffLocationSameAsPickup) return;
        // required only when false
        if (!val.dropoffStreetAddress.trim()) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: 'Required',
                path: ['dropoffStreetAddress'],
            });
        }
        if (!val.dropoffSuburb.trim()) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: 'Required',
                path: ['dropoffSuburb'],
            });
        }
        if (!val.dropoffState) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: 'Required',
                path: ['dropoffState'],
            });
        }
        if (!val.dropoffCountryCode) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: 'Required',
                path: ['dropoffCountryCode'],
            });
        }
        if (!val.dropoffPostcode?.trim()) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: 'Required',
                path: ['dropoffPostcode'],
            });
        }
        if (!val.dropoffPhone?.trim()) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: 'Required',
                path: ['dropoffPhone'],
            });
        }

        if (
            val.dropoffUsesNirovision &&
            val.dropoffNirovisionLocationId === ''
        ) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: 'Required',
                path: ['dropoffNirovisionLocationId'],
            });
        }

        if (val.dropoffUsesNirovision && val.dropoffNirovisionLabelId === '') {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: 'Required',
                path: ['dropoffNirovisionLabelId'],
            });
        }
    });

export const BaseLocationSchema = z.object({
    streetAddress: z.string().min(1, { message: 'Required' }),
    suburb: z.string().min(1, { message: 'Required' }),
    state: z.enum(['NSW', 'QLD', 'TAS', 'VIC', 'ACT', 'WA', 'SA', 'NT'], {
        message: 'Required',
    }),
    countryCode: z.enum(['AU'], { message: 'Required' }),
    postcode: z
        .string()
        .min(1, { message: 'Required' })
        .max(4, { message: 'Invalid postcode' }),
    addressVisible: z.boolean(),
    phone: z
        .string()
        .refine(validatePhone, { message: 'Invalid phone number' }),
    usesNirovision: z.boolean(),
    nirovisionLocationId: z.string(),
    nirovisionLabelId: z.string(),
    nirovisionVehicleLabelId: z.string(),
});

export const LocationSchema = BaseLocationSchema.superRefine(
    validateLocationSchema,
);

export const VehicleLocationSchema = BaseLocationSchema.extend({
    details: VehicleDetails,
}).superRefine(validateLocationSchema);

export const LocationSchemaPartial = BaseLocationSchema.extend({
    streetAddress: z.string(),
    suburb: z.string(),
    postcode: z.string().max(4, { message: 'Invalid postcode' }),
    phone: z
        .string()
        .refine(validatePhone, { message: 'Invalid phone number' })
        .or(z.literal('')),
}).partial();

export const VehicleLocationSchemaPartial = LocationSchemaPartial.extend({
    details: VehicleDetails.optional(),
});
